/**
 * Entity Performance (UC-S10) — billing_account_name roll-ups from regional_client_performance.json.
 */

import type { RegionSummaryRow } from '../types/regionalPerformance'
import {
  parseClientListSummary,
  type RegionalPerformanceRow,
  type ParsedRegionalClient,
} from './regionalClientPerformanceUtils'

export type { RegionSummaryRow } from '../types/regionalPerformance'

export interface BillingAccountContact {
  contact_id: number | string
  billing_account_name?: string
}

export function parseBillingAccountInfo(jsonStr: string | undefined): BillingAccountContact[] {
  if (!jsonStr || typeof jsonStr !== 'string') return []
  try {
    const arr = JSON.parse(jsonStr) as unknown
    return Array.isArray(arr) ? (arr as BillingAccountContact[]) : []
  } catch {
    return []
  }
}

/** contact_id string → billing_account_name */
export function contactIdToBillingAccount(
  jsonStr: string | undefined
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const acc of parseBillingAccountInfo(jsonStr)) {
    const name = acc.billing_account_name?.trim()
    if (name) out[String(acc.contact_id)] = name
  }
  return out
}

/** Stable key for React / maps (entity display name may contain special chars). */
export function entitySelectionKey(entityName: string): string {
  return `e:${encodeURIComponent(entityName)}`
}

export function entityNameFromSelectionKey(key: string): string {
  if (key.startsWith('e:')) return decodeURIComponent(key.slice(2))
  return key
}

export function filterByRegionStrict(
  timeFiltered: RegionalPerformanceRow[],
  selectedRegion: string
): RegionalPerformanceRow[] {
  if (selectedRegion === 'all') return timeFiltered
  return timeFiltered.filter(item => item?.region === selectedRegion)
}

function aggregateParsedBatches(
  batches: ParsedRegionalClient[][]
): ParsedRegionalClient[] {
  const clientAggregates: Record<string, ParsedRegionalClient & { monthCount: number }> =
    {}
  for (const batch of batches) {
    for (const client of batch) {
      const key = client.name.trim()
      if (!clientAggregates[key]) {
        clientAggregates[key] = { ...client, monthCount: 0 }
      }
      const a = clientAggregates[key]
      a.revenueUSD += client.revenueUSD
      a.invoicedUSD += client.invoicedUSD || 0
      a.revenueOriginal += client.revenueOriginal
      a.invoicedOriginal += client.invoicedOriginal
      a.outstanding += client.outstanding
      a.outstandingOriginal += client.outstandingOriginal
      a.monthCount += 1
    }
  }
  return Object.values(clientAggregates).sort((a, b) => b.revenueUSD - a.revenueUSD)
}

/**
 * Summary rows per billing entity (USD roll-up; display stays USD on parent row).
 */
export function buildEntitySummaryRows(
  slice: RegionalPerformanceRow[]
): RegionSummaryRow[] {
  type Acc = {
    revenue: number
    outstanding: number
    invoiced: number
    clientIds: Set<string>
  }
  const map = new Map<string, Acc>()

  for (const item of slice) {
    if (!item?.client_list_summary || !item.billing_account_info) continue
    const contactMap = contactIdToBillingAccount(item.billing_account_info)
    const clients = parseClientListSummary(item.client_list_summary)
    for (const c of clients) {
      const entity = contactMap[c.clientId]
      if (!entity) continue
      let a = map.get(entity)
      if (!a) {
        a = { revenue: 0, outstanding: 0, invoiced: 0, clientIds: new Set() }
        map.set(entity, a)
      }
      a.revenue += c.revenueUSD
      a.outstanding += c.outstanding
      a.invoiced += c.invoicedUSD || 0
      if (c.clientId) a.clientIds.add(c.clientId)
    }
  }

  const totalRev = [...map.values()].reduce((s, x) => s + x.revenue, 0)
  const rows: RegionSummaryRow[] = []

  for (const [entityName, a] of map) {
    const key = entitySelectionKey(entityName)
    const short =
      entityName.length > 24 ? `${entityName.slice(0, 22)}…` : entityName
    rows.push({
      selectionKey: key,
      displayCode: short,
      regionName: entityName,
      revenue: a.revenue,
      outstanding: a.outstanding,
      totalInvoiced: a.invoiced,
      collectionRate: a.invoiced > 0 ? (a.revenue / a.invoiced) * 100 : 0,
      uniqueClients: a.clientIds.size,
      shareOfRevenuePct: totalRev > 0 ? (a.revenue / totalRev) * 100 : 0,
      displayCurrency: 'USD',
      revenueDisplay: a.revenue,
      invoicedDisplay: a.invoiced,
      collectionRateDisplay:
        a.invoiced > 0 ? (a.revenue / a.invoiced) * 100 : 0,
    })
  }

  return rows.sort((x, y) => y.revenue - x.revenue)
}

export function buildClientsByEntityKey(
  slice: RegionalPerformanceRow[],
  summaries: RegionSummaryRow[]
): Record<string, ParsedRegionalClient[]> {
  const out: Record<string, ParsedRegionalClient[]> = {}

  for (const s of summaries) {
    const entityName = entityNameFromSelectionKey(s.selectionKey)
    const batches: ParsedRegionalClient[][] = []

    for (const row of slice) {
      if (!row.client_list_summary || !row.billing_account_info) continue
      const contactMap = contactIdToBillingAccount(row.billing_account_info)
      const clients = parseClientListSummary(row.client_list_summary)
      const subset = clients.filter(c => contactMap[c.clientId] === entityName)
      if (subset.length > 0) batches.push(subset)
    }

    out[s.selectionKey] =
      batches.length === 0 ? [] : aggregateParsedBatches(batches)
  }

  return out
}

export function extractRegionsForEntityPage(
  data: RegionalPerformanceRow[]
): string[] {
  const set = new Set<string>()
  for (const item of data) {
    if (item?.region) set.add(item.region)
  }
  return [...set].sort()
}
